import { RepeatableListInput } from './RepeatableListInput';
import { FileUploadZone, type UploadedFile } from './FileUploadZone';

interface ProductData {
  description: string;
  valueProposition: string;
  onboardingProcess: string;
  cost: string;
  pricingDetails: string;
  topFeedbackItems: string[];
}

interface ProductSectionProps {
  data: ProductData;
  onChange: (data: ProductData) => void;
  questionnaireId: string;
  uploads: UploadedFile[];
  onUploadsChange: (uploads: UploadedFile[]) => void;
}

export function ProductSection({
  data,
  onChange,
  questionnaireId,
  uploads,
  onUploadsChange,
}: ProductSectionProps) {
  const update = <K extends keyof ProductData>(field: K, value: ProductData[K]) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Describe your product
        </label>
        <textarea
          value={data.description}
          onChange={(e) => update('description', e.target.value)}
          rows={4}
          placeholder="What does your product do?"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Value Proposition</label>
        <textarea
          value={data.valueProposition}
          onChange={(e) => update('valueProposition', e.target.value)}
          rows={3}
          placeholder="What is the core value proposition?"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Onboarding Process</label>
        <textarea
          value={data.onboardingProcess}
          onChange={(e) => update('onboardingProcess', e.target.value)}
          rows={3}
          placeholder="Is there any customer onboarding? What does it look like?"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Product Cost</label>
        <input
          type="text"
          value={data.cost}
          onChange={(e) => update('cost', e.target.value)}
          placeholder="e.g., Starting at $99/month"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Full Pricing Details</label>
        <textarea
          value={data.pricingDetails}
          onChange={(e) => update('pricingDetails', e.target.value)}
          rows={4}
          placeholder="Describe all tiers, add-ons, discounts..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
        />
      </div>

      <RepeatableListInput
        label="Top 5 Customer Feedback Items"
        items={data.topFeedbackItems}
        onChange={(items) => update('topFeedbackItems', items)}
        maxItems={5}
        placeholder="Add feedback item..."
      />

      <div className="pt-2">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Product Documents (manuals, descriptions, etc.)
        </label>
        <FileUploadZone
          questionnaireId={questionnaireId}
          section="product"
          uploads={uploads}
          onUploadsChange={onUploadsChange}
        />
      </div>
    </div>
  );
}
